import React from "react";

// AUTORIZAÇÃO POR ROTA (TD-031)
// -----------------------------
// Além de `secured` (exige estar logado), cada rota pode declarar:
//
//   right: "listBookings"   → exige o direito. Usuário master passa por cima.
//   masterOnly: true        → exclusivo da SMI.
//
// Quem aplica é o <RouteGuard> nos layouts. Antes disso, o direito só era checado onde o botão do
// menu é DESENHADO — ou seja, quem digitasse a URL entrava. Não é segurança (o backend é quem
// protege dado, e escopa por token); é a interface não oferecer o que a pessoa não pode fazer.
//
// Rota sem `right` nem `masterOnly` é aberta a qualquer usuário autenticado — e isso é declaração,
// não esquecimento. As rotas de estoque, configurações, cotações e relatórios ainda NÃO foram
// anotadas: exigem conferir o direito real com o backend antes, e anotar errado tranca alguém para
// fora de uma tela que ele usa hoje. Ver TD-031.

// Screens — carregadas sob demanda (code-splitting via React.lazy / P1).
// Cada chunk só é baixado quando a rota correspondente é acessada, em vez de
// tudo (admin + pagamento + mapas) ir no bundle inicial da tela de login.
// O <Suspense> + <ChunkErrorBoundary> que tratam o carregamento ficam nos
// layouts (Desktop.js / Mobile.js), em volta do <Routes>.
const Home = React.lazy(() => import("./Screens/Home/Main.js"));
const Order = React.lazy(() => import("./Screens/Order/Main.js"));

const OrderHotelSearchResult = React.lazy(() => import("./Screens/Order/hotelSearchResult.js"));
const OrderHotelHotelView = React.lazy(() => import("./Screens/Order/hotelDetails.js"));

const OrderHotelBooking = React.lazy(() => import("./Screens/Order/hotelBooking.js"));
const OrderHotelBookingConfirm = React.lazy(() => import("./Screens/Order/hotelBookingConfirm.js"));
const OrderHotelBookingPayment = React.lazy(() => import("./Screens/Order/hotelBookingPayment.js"));

const UserPasswordSetup = React.lazy(() => import("./Screens/User/PasswordSetup.js"));

const Admin = React.lazy(() => import("./Screens/Admin/Main.js"));
const AdminReservations = React.lazy(() => import("./Screens/Admin/Reservations.js"));
const AdminCancellationDeadlines = React.lazy(() => import("./Screens/Admin/CancellationDeadlines.js"));
const AdminAutoCancel = React.lazy(() => import("./Screens/Admin/AutoCancel.js"));
const AdminAgencies = React.lazy(() => import("./Screens/Admin/Agencies.js"));
const AdminAgenciesApproval = React.lazy(() => import("./Screens/Admin/AgenciesApproval.js"));

const AdminUserProfile = React.lazy(() => import("./Screens/Admin/UserProfile.js"));
const AdminAgencyProfile = React.lazy(() => import("./Screens/Admin/AgencyProfile.js"));

const AdminDashboard = React.lazy(() => import("./Screens/Admin/Dashboard.js"));
const AdminUsers = React.lazy(() => import("./Screens/Admin/Users.js"));
const AdminMasterUsers = React.lazy(() => import("./Screens/Admin/MasterUsers.js"));

const AdminCustomers = React.lazy(() => import("./Screens/Admin/Customers.js"));
const AdminReports = React.lazy(() => import("./Screens/Admin/Reports.js"));
const AdminConfigurations = React.lazy(() => import("./Screens/Admin/Configurations.js"));
const AdminQuotes = React.lazy(() => import("./Screens/Admin/Quotes.js"));
const AdminQuotesFase2Demo = React.lazy(() => import("./Screens/Admin/QuotesFase2Demo.js"));
const AdminConfiguracoes = React.lazy(() => import("./Screens/Admin/Configuracoes.js"));
const AdminStock = React.lazy(() => import("./Screens/Admin/StockManagement.js"));
const AdminStockProperties = React.lazy(() => import("./Screens/Admin/StockProperties.js"));
const AdminStockCountries = React.lazy(() => import("./Screens/Admin/StockCountries.js"));
const AdminStockExceptionRules = React.lazy(() => import("./Screens/Admin/StockExceptionRules.js"));

var dashRoutes = [
  {
    path: "/home",
    name: "Início",
    component: Home,
    secured: false
  },
  {
    path: "/order",
    name: "Defina como será a estadia",
    component: Order,
    secured: true
  },
  {
    path: "/order/hotel/searchResult",
    name: "Compra de Hospedagem > Buscando",
    component: OrderHotelSearchResult,
    secured: true
  },
  {
    path: "/order/hotel/view/:id",
    name: "Compra de Hospedagem > Visualizando Hotel",
    component: OrderHotelHotelView,
    secured: true
  },
  {
    path: "/order/hotel/booking",
    name: "Compra de Hospedagem > Reservando Hotel",
    component: OrderHotelBooking,
    secured: true
  },
  {
    path: "/order/hotel/booking/confirm",
    name: "Compra de Hospedagem > Confirmação de Reserva",
    component: OrderHotelBookingConfirm,
    secured: true
  },
  {
    path: "/order/hotel/booking/payment",
    name: "Compra de Hospedagem > Pagamento",
    component: OrderHotelBookingPayment,
    secured: true
  },
  {
    path: "/admin",
    name: "Área administrativa",
    component: Admin,
    secured: true
  },
  {
    path: "/admin/dashboard",
    name: "Área administrativa - Dashboard",
    component: AdminDashboard,
    secured: true
  },
  {
    path: "/admin/agencies",
    name: "Área administrativa - Agências",
    component: AdminAgencies,
    right: "listAgencies",
    secured: true
  },
  {
    path: "/admin/agenciesApproval",
    name: "Área administrativa - Agências aguardando aprovação",
    component: AdminAgenciesApproval,
    right: "listApprovalAgencies",
    secured: true
  },
  {
    path: "/admin/userProfile",
    name: "Área administrativa - Perfil do usuário",
    component: AdminUserProfile,
    secured: true
  },
  {
    path: "/admin/agencyProfile",
    name: "Área administrativa - Perfil da Agência",
    component: AdminAgencyProfile,
    secured: true
  },
  {
    path: "/admin/users",
    name: "Área administrativa - Usuários da Agência",
    component: AdminUsers,
    right: "manageUsers",
    secured: true
  },
  {
      path: "/admin/masterUsers",
      name: "Área administrativa - Usuários Agência Master",
      component: AdminMasterUsers,
      masterOnly: true,
      right: "manageUsers",
      secured: true
  },
{
    path: "/admin/customers",
    name: "Área administrativa - Clientes da Agência",
    component: AdminCustomers,
    secured: true
  },
  {
    path: "/admin/reservations",
    name: "Área administrativa - Reservas",
    component: AdminReservations,
    right: "listBookings",
    secured: true
  },
  {
    path: "/admin/prazos-cancelamento",
    name: "Área administrativa - Prazos de cancelamento",
    component: AdminCancellationDeadlines,
    right: "listBookings",
    secured: true
  },
  {
    path: "/admin/cancelamento-automatico",
    name: "Área administrativa - Cancelamento automático",
    component: AdminAutoCancel,
    masterOnly: true,
    secured: true
  },
  {
    path: "/admin/cotacoes",
    name: "Área administrativa - Cotações",
    component: AdminQuotes,
    secured: true
  },
  {
    path: "/admin/cotacoes-fase2",
    name: "Área administrativa - Cotações (Fase 2 — demo)",
    component: AdminQuotesFase2Demo,
    secured: true
  },
  {
    path: "/admin/reports",
    name: "Área administrativa - Relatórios",
    component: AdminReports,
    secured: true
  },
  {
    path: "/admin/configurations",
    name: "Área administrativa - Configurações",
    component: AdminConfigurations,
    secured: true
  },
  {
    path: "/admin/configuracoes",
    name: "Área administrativa - Configurações",
    component: AdminConfiguracoes,
    secured: true
  },
  {
    path: "/admin/configuracoes/:group",
    name: "Área administrativa - Configurações",
    component: AdminConfiguracoes,
    secured: true
  },
  {
    path: "/admin/estoque",
    name: "Área administrativa - Propriedades em estoque",
    component: AdminStockProperties,
    secured: true
  },
  {
    path: "/admin/estoque/propriedades",
    name: "Área administrativa - Propriedades em estoque",
    component: AdminStockProperties,
    secured: true
  },
  {
    path: "/admin/estoque/gerar",
    name: "Área administrativa - Gerenciar Estoque",
    component: AdminStock,
    secured: true
  },
  {
    path: "/admin/estoque/paises",
    name: "Área administrativa - Países disponíveis",
    component: AdminStockCountries,
    secured: true
  },
  {
    path: "/admin/estoque/regras",
    name: "Área administrativa - Regras de exceção",
    component: AdminStockExceptionRules,
    secured: true
  },
  {
    path: "/usuario/definir-senha/:token",
    name: "Definir Senha",
    component: UserPasswordSetup,
    secured: false
  }
];
export default dashRoutes;
